/**
 * Reactions — the audience's only voice.
 *
 * An audience member cannot speak (no microphone grant) and is `hidden`, so they
 * never even appear in the room. Reactions are therefore not decoration: they are
 * the entire feedback channel between a comedian and the people listening. A set
 * that is too large dilutes the signal, so it is deliberately five.
 *
 * Sent over LiveKit's data channel as LOSSY packets. Dropping a reaction under
 * congestion is the correct behaviour — a late clap is worse than no clap, and
 * media must never be delayed behind a backlog of emoji.
 */

import { type Room, DataPacket_Kind } from 'livekit-client';

export const REACTIONS = [
  { id: 'laugh', glyph: '😂', label: 'Laughing' },
  { id: 'clap', glyph: '👏', label: 'Applause' },
  { id: 'fire', glyph: '🔥', label: 'Fire' },
  { id: 'love', glyph: '❤️', label: 'Love' },
  { id: 'wow', glyph: '😮', label: 'Wow' },
] as const;

export type ReactionId = (typeof REACTIONS)[number]['id'];

const VALID = new Set<string>(REACTIONS.map((r) => r.id));
const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Rate limit per sender. Enthusiasm is fine; a held-down button is not. */
const MIN_INTERVAL_MS = 250;
let lastSent = 0;

export function sendReaction(room: Room, id: ReactionId): boolean {
  const now = Date.now();
  if (now - lastSent < MIN_INTERVAL_MS) return false;
  lastSent = now;

  const payload = encoder.encode(JSON.stringify({ t: 'reaction', id }));
  // Fire and forget: awaiting a lossy packet buys nothing.
  void room.localParticipant.publishData(payload, { reliable: false });
  return true;
}

export interface IncomingReaction {
  id: ReactionId;
  from: string;
}

/**
 * Parse a data packet into a reaction, or null.
 *
 * Everything here is untrusted — it arrives from another participant, and
 * identities are unauthenticated. The id is checked against the known set so a
 * crafted packet cannot inject an arbitrary string into the UI.
 */
export function parseReaction(
  payload: Uint8Array,
  from: string | undefined,
  kind?: DataPacket_Kind,
): IncomingReaction | null {
  if (kind === DataPacket_Kind.RELIABLE) return null; // reactions are lossy only
  try {
    const message = JSON.parse(decoder.decode(payload)) as { t?: string; id?: string };
    if (message.t !== 'reaction' || !message.id || !VALID.has(message.id)) return null;
    return { id: message.id as ReactionId, from: from ?? 'someone' };
  } catch {
    return null;
  }
}
